import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_BASE = '/api';
const api = {
  get:  (p: string) => axios.get(API_BASE + p),
  put:  (p: string, d: any) => axios.put(API_BASE + p, d),
  post: (p: string, d?: any) => axios.post(API_BASE + p, d),
};

type Config = {
  enabled: boolean; azure_tenant_id: string; azure_client_id: string;
  sso_enabled: boolean; presence_sync_enabled: boolean;
  contacts_sync_enabled: boolean; meetings_sync_enabled: boolean;
  has_client_secret: boolean;
};
const EMPTY: Config = {
  enabled: false, azure_tenant_id: '', azure_client_id: '',
  sso_enabled: false, presence_sync_enabled: false,
  contacts_sync_enabled: false, meetings_sync_enabled: false, has_client_secret: false,
};

export default function TeamsSettingsPage({ user }: any) {
  const [cfg, setCfg] = useState<Config>(EMPTY);
  const [secret, setSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const flash = (text: string, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg(null), 5000); };

  useEffect(() => {
    api.get('/teams/config').then((r: any) => setCfg({ ...EMPTY, ...r.data })).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body: any = {
        enabled: cfg.enabled, azure_tenant_id: cfg.azure_tenant_id,
        azure_client_id: cfg.azure_client_id, sso_enabled: cfg.sso_enabled,
        presence_sync_enabled: cfg.presence_sync_enabled,
        contacts_sync_enabled: cfg.contacts_sync_enabled,
        meetings_sync_enabled: cfg.meetings_sync_enabled,
      };
      if (secret) body.azure_client_secret = secret;
      const r: any = await api.put('/teams/config', body);
      setCfg({ ...EMPTY, ...r.data });
      setSecret('');
      flash('Configuration saved');
    } catch (e: any) { flash(e.response?.data?.error || e.message, false); }
    finally { setSaving(false); }
  };

  const validate = async () => {
    setValidating(true);
    try {
      const r: any = await api.post('/teams/config/validate');
      flash(r.data.valid ? 'Azure credentials valid — Graph API connected' : `Invalid: ${r.data.error}`, r.data.valid);
    } catch (e: any) { flash(e.response?.data?.error || e.message, false); }
    finally { setValidating(false); }
  };

  const syncContacts = async () => {
    setSyncing('contacts');
    try {
      const r: any = await api.post('/teams/contacts/sync');
      flash(`Contacts synced: ${r.data.added} added, ${r.data.updated} updated`);
    } catch (e: any) { flash(e.response?.data?.error || e.message, false); }
    finally { setSyncing(null); }
  };

  const syncPresence = async () => {
    setSyncing('presence');
    try {
      const r: any = await api.post('/teams/presence/sync');
      flash(`Presence synced: ${r.data.synced} users updated`);
    } catch (e: any) { flash(e.response?.data?.error || e.message, false); }
    finally { setSyncing(null); }
  };

  const card = (title: string, children: React.ReactNode) => (
    <div style={{ background: '#1e293b', borderRadius: 12, padding: 24, marginBottom: 20 }}>
      <h3 style={{ color: '#f1f5f9', margin: '0 0 18px', fontSize: 15, fontWeight: 600 }}>{title}</h3>
      {children}
    </div>
  );

  const field = (label: string, el: React.ReactNode) => (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', color: '#94a3b8', fontSize: 12, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</label>
      {el}
    </div>
  );

  const inp = (val: string, onChange: (v: string) => void, placeholder = '', type = 'text') => (
    <input type={type} value={val} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 8,
        color: '#f1f5f9', padding: '10px 14px', fontSize: 13, boxSizing: 'border-box' }} />
  );

  const tog = (checked: boolean, onChange: (v: boolean) => void, label: string) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: '#cbd5e1', fontSize: 13, marginBottom: 12 }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        style={{ width: 16, height: 16, accentColor: '#6366f1' }} />
      {label}
    </label>
  );

  const btn = (label: string, onClick: () => void, disabled = false, color = '#6366f1') => (
    <button onClick={onClick} disabled={disabled}
      style={{ background: disabled ? '#334155' : color, color: '#fff', border: 'none', borderRadius: 8,
        padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', marginRight: 10 }}>
      {label}
    </button>
  );

  return (
    <div style={{ padding: '28px 32px', maxWidth: 760 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
        <span style={{ fontSize: 32 }}>🔷</span>
        <div>
          <h2 style={{ color: '#f1f5f9', margin: 0, fontSize: 22, fontWeight: 700 }}>Microsoft Teams Integration</h2>
          <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: 13 }}>
            Presence sync, contacts &amp; meetings. Voice calls remain on the SIP telephony system.
          </p>
        </div>
      </div>

      {msg && (
        <div style={{ background: msg.ok ? '#14532d' : '#7f1d1d', border: `1px solid ${msg.ok ? '#22c55e' : '#ef4444'}`,
          borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: msg.ok ? '#bbf7d0' : '#fecaca', fontSize: 13 }}>
          {msg.text}
        </div>
      )}

      {card('Integration Status', <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: cfg.enabled ? '#22c55e' : '#ef4444' }} />
          <span style={{ color: cfg.enabled ? '#22c55e' : '#ef4444', fontWeight: 600, fontSize: 13 }}>
            {cfg.enabled ? 'Teams Integration Enabled' : 'Teams Integration Disabled'}
          </span>
        </div>
        {tog(cfg.enabled, v => setCfg({ ...cfg, enabled: v }), 'Enable Microsoft Teams Integration for this tenant')}
      </>)}

      {card('Azure App Registration (Microsoft Entra ID)', <>
        <p style={{ color: '#94a3b8', fontSize: 12, margin: '0 0 16px', lineHeight: 1.6 }}>
          Required Graph permissions —
          <strong style={{ color: '#c084fc' }}> User.Read.All</strong>,{' '}
          <strong style={{ color: '#c084fc' }}>Presence.Read.All</strong> (Application);{' '}
          <strong style={{ color: '#c084fc' }}>User.Read</strong>,{' '}
          <strong style={{ color: '#c084fc' }}>Presence.Read</strong>,{' '}
          <strong style={{ color: '#c084fc' }}>offline_access</strong> (Delegated)
        </p>
        {field('Azure Tenant ID (Directory ID)', inp(cfg.azure_tenant_id, v => setCfg({ ...cfg, azure_tenant_id: v }), 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'))}
        {field('Client ID (Application ID)', inp(cfg.azure_client_id, v => setCfg({ ...cfg, azure_client_id: v }), 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'))}
        {field(cfg.has_client_secret ? 'Client Secret (blank = keep existing)' : 'Client Secret',
          inp(secret, setSecret, cfg.has_client_secret ? '••••••••••••••••••••••••' : 'Paste client secret value', 'password')
        )}
        {cfg.has_client_secret && <p style={{ color: '#22c55e', fontSize: 11, margin: '-8px 0 12px' }}>✓ Secret saved (AES-256-GCM encrypted)</p>}
        <p style={{ color: '#475569', fontSize: 11, margin: '4px 0 0' }}>
          OAuth callback URL: <code style={{ color: '#818cf8' }}>/api/teams/auth/callback</code>
        </p>
      </>)}

      {card('Features', <>
        {tog(cfg.sso_enabled, v => setCfg({ ...cfg, sso_enabled: v }), 'Single Sign-On — users can log in via Microsoft account')}
        {tog(cfg.presence_sync_enabled, v => setCfg({ ...cfg, presence_sync_enabled: v }), 'Presence Sync — show Teams availability on contacts (auto every 3 min)')}
        {tog(cfg.contacts_sync_enabled, v => setCfg({ ...cfg, contacts_sync_enabled: v }), 'Contact Sync — import Microsoft directory (auto every 6 hours)')}
        {tog(cfg.meetings_sync_enabled, v => setCfg({ ...cfg, meetings_sync_enabled: v }), 'Meeting Creation — create Teams meetings from within the platform')}
      </>)}

      <div style={{ marginBottom: 24 }}>
        {btn(saving ? 'Saving...' : 'Save Configuration', save, saving)}
        {btn(validating ? 'Validating...' : 'Test Azure Connection', validate, validating || !cfg.azure_client_id, '#0f766e')}
      </div>

      {cfg.enabled && cfg.has_client_secret && card('Manual Sync', <>
        <p style={{ color: '#94a3b8', fontSize: 12, margin: '0 0 16px' }}>
          Contacts sync automatically every 6 hours; presence every 3 minutes. Trigger immediately here.
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          {btn(syncing === 'contacts' ? 'Syncing...' : 'Sync Contacts Now', syncContacts, syncing !== null || !cfg.contacts_sync_enabled, '#1d4ed8')}
          {btn(syncing === 'presence' ? 'Syncing...' : 'Sync Presence Now', syncPresence, syncing !== null || !cfg.presence_sync_enabled, '#7c3aed')}
        </div>
      </>)}
    </div>
  );
}
